/**
 * SignaturePad — the Type / Draw / Upload signature pad (UX-DR17), shared by the CB-3.6 stored-
 * signature settings surface and (later) the Epic 5 signing ceremony (AD-16). Controlled: the parent
 * holds a SignatureValue; the active tab determines which representation is live and clears the
 * others so the saved value is unambiguous (one of typed+style OR an image data-URL).
 */

import { useEffect, useRef, useState } from 'react';
import SignaturePadLib from 'signature_pad';
import { Eraser, Upload as UploadIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  type SignatureValue,
  SIGNATURE_STYLES,
  DEFAULT_SIGNATURE_STYLE,
} from '@/components/settings/contracts/storedSignature';

// 1MB cap keeps the inline data-URL row sane (v1 choice — drawn/uploaded images stored inline in
// signature_image; a private-bucket ref per AD-21 is a later optimization, column contract unchanged).
const UPLOAD_MAX_BYTES = 1024 * 1024;

/** Approx decoded byte size of a base64 data-URL (4 base64 chars ≈ 3 bytes), for the same 1MB cap. */
const dataUrlBytes = (dataUrl: string): number => {
  const comma = dataUrl.indexOf(',');
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  return Math.floor((b64.length * 3) / 4);
};

type Tab = 'type' | 'draw' | 'upload';

interface SignaturePadProps {
  value: SignatureValue;
  onChange: (v: SignatureValue) => void;
  /** Seed for the Type tab's name input (e.g. the user's name). */
  typedNameDefault?: string;
}

export function SignaturePad({ value, onChange, typedNameDefault }: SignaturePadProps) {
  const [tab, setTab] = useState<Tab>(value.signature_image ? 'draw' : 'type');

  // --- Type ---
  const [typedName, setTypedName] = useState(value.signature_typed ?? typedNameDefault ?? '');
  const [styleKey, setStyleKey] = useState(value.signature_style ?? DEFAULT_SIGNATURE_STYLE);
  const emitTyped = (name: string, style: string) =>
    onChange({
      signature_typed: name.trim() ? name : null,
      signature_style: name.trim() ? style : null,
      signature_image: null,
      signature_method: name.trim() ? 'typed' : null, // FR-56 method capture (CB-5.10)
    });

  // --- Draw (signature_pad) ---
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const padRef = useRef<SignaturePadLib | null>(null);
  useEffect(() => {
    if (tab !== 'draw' || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')?.scale(ratio, ratio);
    const pad = new SignaturePadLib(canvas, { penColor: '#0b1220' });
    padRef.current = pad;
    const handleEnd = () => {
      if (!pad.isEmpty()) {
        const dataUrl = pad.toDataURL('image/png');
        // Enforce the same 1MB inline cap as the upload path (review S6) — a high-DPI canvas PNG
        // can exceed it; without this the drawn path bypasses the limit the column relies on.
        if (dataUrlBytes(dataUrl) > UPLOAD_MAX_BYTES) {
          toast.error('That signature is too large — try drawing it a little simpler.');
          return;
        }
        onChange({ signature_image: dataUrl, signature_typed: null, signature_style: null, signature_method: 'drawn' });
      }
    };
    pad.addEventListener('endStroke', handleEnd);
    return () => {
      pad.removeEventListener('endStroke', handleEnd);
      padRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const clearDrawing = () => {
    padRef.current?.clear();
    onChange({ signature_image: null, signature_typed: null, signature_style: null, signature_method: null });
  };

  // --- Upload ---
  const fileRef = useRef<HTMLInputElement | null>(null);
  const handleUpload = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Please choose an image file.');
    if (file.size > UPLOAD_MAX_BYTES) return toast.error('That image is too large — keep it under 1MB.');
    const reader = new FileReader();
    reader.onload = () =>
      onChange({ signature_image: String(reader.result), signature_typed: null, signature_style: null, signature_method: 'uploaded' });
    reader.onerror = () => toast.error("Couldn't read that image. Try another file.");
    reader.readAsDataURL(file);
  };

  return (
    <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
      <TabsList>
        <TabsTrigger value="type">Type</TabsTrigger>
        <TabsTrigger value="draw">Draw</TabsTrigger>
        <TabsTrigger value="upload">Upload</TabsTrigger>
      </TabsList>

      <TabsContent value="type" className="space-y-3 pt-3">
        <Input
          aria-label="Your name"
          placeholder="Type your name"
          value={typedName}
          onChange={(e) => {
            setTypedName(e.target.value);
            emitTyped(e.target.value, styleKey);
          }}
        />
        <div className="grid gap-2 sm:grid-cols-3">
          {SIGNATURE_STYLES.map((s) => (
            <button
              key={s.key}
              type="button"
              aria-pressed={styleKey === s.key}
              onClick={() => {
                setStyleKey(s.key);
                emitTyped(typedName, s.key);
              }}
              className={cn(
                'rounded-lg border p-2 text-center transition-colors',
                styleKey === s.key ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:bg-accent/50',
              )}
            >
              <span className="block truncate text-2xl leading-tight text-foreground" style={{ fontFamily: s.fontFamily }}>
                {typedName.trim() || 'Your name'}
              </span>
              <span className="text-[11px] text-muted-foreground">{s.label}</span>
            </button>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="draw" className="space-y-2 pt-3">
        <div className="rounded-lg border border-border bg-card">
          {/* NFR-14/A14: the canvas is an image to AT, with instructions pointing keyboard/SR users at
              the Type tab so they're never trapped on a pointer-only surface. touch-none stops the page
              from scrolling/zooming while a finger draws (NFR-3 touch-sig). */}
          <canvas
            ref={canvasRef}
            role="img"
            aria-label="Draw your signature with your finger or mouse. Or switch to the Type tab to enter it with the keyboard."
            className="h-40 w-full touch-none rounded-lg"
          />
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={clearDrawing}>
          <Eraser className="mr-1.5 h-4 w-4" /> Clear
        </Button>
      </TabsContent>

      <TabsContent value="upload" className="space-y-2 pt-3">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files?.[0])}
        />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
          <UploadIcon className="mr-1.5 h-4 w-4" /> Choose image
        </Button>
        {tab === 'upload' && value.signature_image && (
          <img
            src={value.signature_image}
            alt="Signature preview"
            className="h-20 rounded border border-border bg-white object-contain p-1"
          />
        )}
        <p className="text-xs text-muted-foreground">PNG or JPG, transparent background works best. Up to 1MB.</p>
      </TabsContent>
    </Tabs>
  );
}
